/**
 * The Learning Center content types (plan docs/plans/lms-plan.md Appendix E,
 * 6.1, 5.2; WP-15): the course document and the markdown subset's tokens,
 * written by hand as the TypeScript twin of
 * backend/src/services/lms/content/schema.js and markdown.js.
 *
 * Kept in step by the shared fixture content/__fixtures__/md-cases.json
 * (byte-identical to backend/tests/fixtures/lms/content/md-cases.json): its
 * "enums" block must equal the constants below and its cases must tokenize
 * the same way here (__tests__/mdLite.test.tsx) and there
 * (backend/tests/unit/lms/content/markdown.test.js).
 *
 * Which strings are markdown (and so go through MdLite, never raw into the
 * DOM): every field ending in _md (body_md, left_md, right_md, steps[].body_md,
 * situation_md, reading_md) is BLOCK markdown; bullets and table columns and
 * cells are INLINE markdown; every other string is plain text.
 *
 * Correct flags (plan 18.11): the learner's content response (GET
 * /courses/:id/content, Appendix A) strips `correct` from the QUIZ QUESTION
 * POOL only. knowledge_check and scenario SLIDES keep their `correct` and
 * `best` flags there, because they are formative and the player gives
 * immediate feedback (7.2); a quiz question's flags reach the learner only as
 * the drawn question's feedback after submission (WP-34). The flags are
 * optional in these types because a quiz option comes without `correct` in
 * the learner's responses; the admin's version response carries every flag.
 */

// ---------------------------------------------------------------------------
// Enums (schema.js; the fixture's "enums" block)
// ---------------------------------------------------------------------------

export const COURSE_CATEGORIES = ['compliance', 'clinical_skills', 'best_practices', 'sop', 'platform', 'custom'] as const;
export type CourseCategory = (typeof COURSE_CATEGORIES)[number];

export const COURSE_KINDS = ['quiz', 'read_ack', 'competency'] as const;
export type CourseKind = (typeof COURSE_KINDS)[number];

/** 'study' exists in the column and is never written by this plan (R15). */
export const COURSE_SCOPES = ['general', 'study'] as const;
export type CourseScope = (typeof COURSE_SCOPES)[number];

export const SLIDE_LAYOUTS = [
  'title',
  'bullets',
  'two_column',
  'callout',
  'steps',
  'table',
  'figure',
  'image',
  'scenario',
  'knowledge_check',
] as const;
export type SlideLayout = (typeof SLIDE_LAYOUTS)[number];

export const CALLOUT_TONES = ['rule', 'risk', 'tip'] as const;
export type CalloutTone = (typeof CALLOUT_TONES)[number];

export const FIGURE_KINDS = ['flow', 'cycle', 'compare', 'hierarchy'] as const;
export type FigureKind = (typeof FIGURE_KINDS)[number];

export const QUESTION_KINDS = ['single', 'multi', 'true_false'] as const;
export type QuestionKind = (typeof QUESTION_KINDS)[number];

export const DIFFICULTIES = ['recall', 'apply', 'analyze'] as const;
export type Difficulty = (typeof DIFFICULTIES)[number];

export const FEEDBACK_MODES = ['after_each', 'at_end'] as const;
export type FeedbackMode = (typeof FEEDBACK_MODES)[number];

/** 5.2: option ids are 'a' to 'f'. */
export const OPTION_IDS = ['a', 'b', 'c', 'd', 'e', 'f'] as const;
export type OptionId = (typeof OPTION_IDS)[number];

export const QUIZ_SETTINGS_KEYS = [
  'pass_mark_pct',
  'draw_count',
  'randomize_questions',
  'randomize_options',
  'max_attempts_per_day',
  'cooldown_minutes',
  'feedback_mode',
  'require_reading',
  'issue_certificate',
] as const;

// ---------------------------------------------------------------------------
// The course document (Appendix E)
// ---------------------------------------------------------------------------

export interface CourseSource {
  title: string;
  publisher: string;
  /** null only for an SOP course citing its own file (11.2). */
  url: string | null;
  /** YYYY-MM-DD */
  accessed_on: string;
  note: string;
}

export interface QuizSettings {
  pass_mark_pct: number;
  draw_count: number;
  randomize_questions: boolean;
  randomize_options: boolean;
  /** null means unlimited (6.5 scenarios). */
  max_attempts_per_day: number | null;
  cooldown_minutes: number;
  feedback_mode: FeedbackMode;
  require_reading: boolean;
  issue_certificate: boolean;
}

/** A read_ack version's quiz settings are {} (5.2). */
export type VersionQuizSettings = QuizSettings | Record<string, never>;

export interface ChoiceOption {
  id: OptionId;
  text: string;
  /** Kept on a knowledge_check slide; absent from a quiz question in the learner's responses (18.11). */
  correct?: boolean;
  feedback: string;
}

export interface ScenarioOption {
  id: OptionId;
  text: string;
  /** Kept on a scenario slide in the learner's content response (18.11). */
  best?: boolean;
  feedback: string;
}

export interface FigureNode {
  id: string;
  label: string;
  note?: string | null;
}

export interface FigureEdge {
  from: string;
  to: string;
  label?: string | null;
}

/** 6.1: a declarative diagram, rendered by components/player/Figure.tsx with divs (D10). */
export interface FigureSpec {
  kind: FigureKind;
  nodes: FigureNode[];
  edges: FigureEdge[];
}

interface SlideBase {
  id: string;
  speaker_notes?: string | null;
}

export interface TitleSlide extends SlideBase {
  layout: 'title';
  heading: string;
  body_md: string;
}

export interface BulletsSlide extends SlideBase {
  layout: 'bullets';
  heading: string;
  /** Inline markdown each. */
  bullets: string[];
}

export interface TwoColumnSlide extends SlideBase {
  layout: 'two_column';
  heading: string;
  left_md: string;
  right_md: string;
}

export interface CalloutSlide extends SlideBase {
  layout: 'callout';
  heading: string;
  tone: CalloutTone;
  body_md: string;
}

export interface StepsSlide extends SlideBase {
  layout: 'steps';
  heading: string;
  steps: Array<{ title: string; body_md: string }>;
}

export interface TableSlide extends SlideBase {
  layout: 'table';
  heading: string;
  /** Inline markdown each. */
  columns: string[];
  /** Inline markdown each; every row has columns.length cells. */
  rows: string[][];
}

export interface FigureSlide extends SlideBase {
  layout: 'figure';
  heading: string;
  figure: FigureSpec;
  text_equivalent: string;
}

export interface ImageSlide extends SlideBase {
  layout: 'image';
  heading: string;
  asset_id: string;
  alt: string;
  caption?: string | null;
}

export interface ScenarioSlide extends SlideBase {
  layout: 'scenario';
  heading: string;
  role: string;
  situation_md: string;
  question: string;
  options: ScenarioOption[];
}

export interface KnowledgeCheckSlide extends SlideBase {
  layout: 'knowledge_check';
  heading?: string | null;
  stem: string;
  kind: QuestionKind;
  options: ChoiceOption[];
  explanation: string;
}

export type Slide =
  | TitleSlide
  | BulletsSlide
  | TwoColumnSlide
  | CalloutSlide
  | StepsSlide
  | TableSlide
  | FigureSlide
  | ImageSlide
  | ScenarioSlide
  | KnowledgeCheckSlide;

export interface CourseSection {
  id: string;
  title: string;
  estimated_minutes: number;
  why_it_matters: string;
  slides: Slide[];
  reading_md: string;
  what_to_remember: string[];
}

export interface GlossaryEntry {
  term: string;
  definition: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
}

/** lms_course_versions.content (5.2). */
export interface CourseContent {
  learning_objectives: string[];
  not_covered: string;
  /** clinical_skills: the 6.3 line. */
  disclaimer?: string | null;
  sections: CourseSection[];
  glossary: GlossaryEntry[];
  key_takeaways: string[];
  /** kind competency only (6.1, R18). */
  competency_checklist?: ChecklistItem[] | null;
}

/** A quiz question as authored (quiz.questions in a file; lms_questions rows). */
export interface CourseQuestion {
  ordinal: number;
  section_id: string;
  objective_index?: number | null;
  kind: QuestionKind;
  stem: string;
  options: ChoiceOption[];
  explanation: string;
  difficulty: Difficulty;
  source_ref?: string | null;
}

/** The builtin catalog file (Appendix E). */
export interface CourseFile {
  slug: string;
  title: string;
  description?: string | null;
  category: CourseCategory;
  kind: CourseKind;
  scope: CourseScope;
  validity_months: number | null;
  renewal_lead_days: number;
  estimated_minutes: number;
  related_course?: string | null;
  change_summary?: string | null;
  sources: CourseSource[];
  content: CourseContent;
  quiz_settings: VersionQuizSettings;
  quiz?: { questions: CourseQuestion[] };
}

// ---------------------------------------------------------------------------
// The markdown subset's tokens (markdown.js; mdLiteTokenizer.ts)
// ---------------------------------------------------------------------------

export type MdInline =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: MdInline[] }
  | { type: 'em'; children: MdInline[] }
  | { type: 'code'; text: string }
  | { type: 'link'; href: string; children: MdInline[] };

export type MdBlock =
  | { type: 'paragraph'; children: MdInline[] }
  | { type: 'heading'; level: 3; children: MdInline[] }
  | { type: 'list'; ordered: boolean; items: MdInline[][] }
  | { type: 'table'; header: MdInline[][]; rows: MdInline[][][] }
  | { type: 'blockquote'; children: MdInline[]; attribution: MdInline[] };

export const MD_ERROR_RULES = [
  'md_type',
  'md_html',
  'md_image',
  'md_footnote',
  'md_reference_link',
  'md_link',
  'md_link_url',
  'md_code',
  'md_emphasis',
  'md_heading',
  'md_hr',
  'md_code_fence',
  'md_indent',
  'md_nested_list',
  'md_list_marker',
  'md_list',
  'md_list_continuation',
  'md_table',
  'md_blockquote',
  'md_blockquote_attribution',
  'md_inline_newline',
] as const;
export type MdErrorRule = (typeof MD_ERROR_RULES)[number];

export interface MdError {
  rule: MdErrorRule;
  /** 1-based */
  line: number;
  message: string;
}

export interface MdBlockResult {
  ok: boolean;
  blocks: MdBlock[];
  errors: MdError[];
}

export interface MdInlineResult {
  ok: boolean;
  inlines: MdInline[];
  errors: MdError[];
}
